// Hand-authored study parts (copycat of the public Appetite UI fitness previews): iOS-like status bar glyphs, activity rings,
// tiny charts and the bottom tab bar. Plain React + SVG + Lucide icons; no UBM primitive, no kit asset.
import type { ReactNode } from "react";
import { BicepsFlexed, ChartLine, Dumbbell, User } from "lucide-react";

export type Route = "accueil" | "programme" | "suivi" | "seance";

export function StatusBar({ tone = "dark" }: { tone?: "dark" | "light" }) {
  const color = tone === "light" ? "#fff" : "#111";
  return <div className="cc-status" style={{ color }} aria-hidden="true">
    <span className="cc-status-time">9:41</span>
    <span className="cc-status-icons">
      <svg width="18" height="12" viewBox="0 0 18 12" fill={color}><rect x="0" y="8" width="3" height="4" rx="1" /><rect x="5" y="5.5" width="3" height="6.5" rx="1" /><rect x="10" y="3" width="3" height="9" rx="1" /><rect x="15" y="0" width="3" height="12" rx="1" /></svg>
      <svg width="16" height="12" viewBox="0 0 16 12" fill={color}><path d="M8 2.6c2.3 0 4.4.9 6 2.4l1.2-1.3A10.2 10.2 0 0 0 8 .8C5.2.8 2.7 1.9.8 3.7L2 5c1.6-1.5 3.7-2.4 6-2.4Zm0 3.6c1.3 0 2.5.5 3.4 1.3l1.2-1.3A6.7 6.7 0 0 0 8 4.4c-1.8 0-3.4.7-4.6 1.8l1.2 1.3c.9-.8 2.1-1.3 3.4-1.3Zm0 3.5c-.5 0-.9.2-1.2.5L8 11.6l1.2-1.4c-.3-.3-.7-.5-1.2-.5Z" /></svg>
      <svg width="27" height="13" viewBox="0 0 27 13" fill="none"><rect x=".5" y=".5" width="23" height="12" rx="3.5" stroke={color} opacity=".4" /><rect x="2" y="2" width="20" height="9" rx="2" fill={color} /><path d="M25 4.5v4c.8-.3 1.3-1.1 1.3-2s-.5-1.7-1.3-2Z" fill={color} opacity=".45" /></svg>
    </span>
  </div>;
}

/** Three concentric progress rings (outer Move blue, middle Exercise green, inner Stand orange). */
export function Rings({ values }: { values: [number, number, number] }) {
  const colors = ["#1b5cff", "#22c55e", "#ff9f0a"];
  const radii = [52, 39, 26];
  return <svg className="cc-rings" width="118" height="118" viewBox="0 0 118 118" aria-hidden="true">
    {radii.map((r, i) => {
      const c = 2 * Math.PI * r;
      return <g key={r} transform="rotate(-90 59 59)">
        <circle cx="59" cy="59" r={r} fill="none" stroke={colors[i]} strokeOpacity=".16" strokeWidth="10" />
        <circle cx="59" cy="59" r={r} fill="none" stroke={colors[i]} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${c * values[i]!} ${c}`} />
      </g>;
    })}
  </svg>;
}

export function HeartLine() {
  return <svg className="cc-heartline" viewBox="0 0 140 20" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 13 C10 12 16 14 24 13 S38 11 46 12 S60 15 70 13 S84 11 92 12 S108 14 118 12 S132 13 140 12" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
  </svg>;
}

export function SleepBars() {
  const bars = [16, 22, 12, 26, 18, 30, 24];
  return <div className="cc-sleepbars" aria-hidden="true">{bars.map((h, i) => <span key={i} style={{ height: h, opacity: i === bars.length - 1 ? 1 : .55 }} />)}</div>;
}

const TABS: { id: string; label: string; icon: ReactNode; route?: Route }[] = [
  { id: "home", label: "Home", icon: <Dumbbell size={21} strokeWidth={1.8} />, route: "accueil" },
  { id: "library", label: "Library", icon: <BicepsFlexed size={21} strokeWidth={1.8} />, route: "programme" },
  { id: "activity", label: "Activity", icon: <ChartLine size={21} strokeWidth={1.8} />, route: "suivi" },
  { id: "profile", label: "Profile", icon: <User size={21} strokeWidth={1.8} /> },
];

export function TabBar({ active, go, onOutOfScope }: { active: "home" | "library" | "activity"; go: (route: Route) => void; onOutOfScope: (label: string) => void }) {
  return <nav className="cc-tabbar" aria-label="Main">
    {TABS.map(tab => <button key={tab.id} type="button" className="cc-tab" data-active={tab.id === active || undefined} aria-current={tab.id === active ? "page" : undefined}
      onClick={() => tab.route ? go(tab.route) : onOutOfScope(tab.label)}>
      {tab.icon}<span>{tab.label}</span>
    </button>)}
  </nav>;
}
